import React, { useState, useEffect } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import { useBreakpointUp } from '@shared/hooks/useBreakpoint';
import classNames from 'classnames';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

import { SearchForm } from '@shared/components/SearchForm';

import {
    useUsersGridUIStore,
    useUserUIStore,
    useUserPermissionsUIStore,
    useProceduresUIStore,
} from '@core/useStores';
import { useStyles } from './UsersPage.styles';
import { UsersFiltersModal } from '../UsersFiltersModal';
import { UsersList } from '../UsersList/UsersList';
import { UsersSummary } from '../../../@shared/components/UsersSummary/UsersSummary';

import { messages } from 'Users/users.messages';
import { UsersExport } from '../UsersExport';
import { UsersImport } from '../UsersImport';
import { ThemeProps } from '@styles/theme';
import { Button } from '@shared/components/Button';

export const UsersPage: React.FC = () => {
    const [filtersModalOpen, toggleFiltersModalOpen] = useState(false);

    const userPermissionsUIStore = useUserPermissionsUIStore();
    const usersGridUIStore = useUsersGridUIStore();
    const userUIStore = useUserUIStore();
    const proceduresGridUIStore = useProceduresUIStore();


    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const showSummary = useBreakpointUp('desktop');

    const onUsersSearchTextChanged = (searchText: string) => {
        if (searchText && searchText.length === 1) {
            return;
        }

        usersGridUIStore.setParams({
            page: 0,
            searchText,
        });
    };

    const onInviteUserModalOpen = () => {
        userUIStore.clearEditUserId();
        userUIStore.toggleUserModalOpen(true);
    };

    const setGroupFilter = (inGroup: number, inGroupId?: string) => {
        if(inGroupId){
            usersGridUIStore.setParams({
                page: 0,
                inGroup,
                inGroupId: inGroupId,
            });
            proceduresGridUIStore.setParams({
                page: 0,
                inGroup,
                inGroupId: inGroupId,
            });
        }else{
            usersGridUIStore.setParams({
                page: 0,
                inGroup,
                inGroupId: undefined,
            });
            proceduresGridUIStore.setParams({
                page: 0,
                inGroup,
                inGroupId: undefined,
            });
        }
    }

    useEffect(() => {
        setGroupFilter(0);
    }, []);

    return (
        <div className={styles.mainWrapper}>
            <div className={styles.tableActionsContainer}>
                <div className={styles.tableActions}>
                    <div className={styles.filters}>
                        <Observer>
                            {() => (
                                <>
                                    <SearchForm
                                        initialValues={{
                                            search: usersGridUIStore.requestParams
                                                .getStorageParams
                                                ? usersGridUIStore.requestParams
                                                    .getStorageParams.searchText
                                                : '',
                                        }}
                                        onSearchTextChanged={onUsersSearchTextChanged}
                                        className={styles.searchWrapper}
                                    />
                                </>
                            )}
                        </Observer>

                        <span
                            style={{ display: 'inherit' }}
                            title={messages['usersPage.filter.title']}
                        >
                            <FilterListOutlinedIcon
                                onClick={() => toggleFiltersModalOpen(true)}
                                className={styles.filterIcon}
                            />
                        </span>
                    </div>
                    {showSummary &&
                        <UsersSummary
                            statistics={usersGridUIStore.statistics}
                            pageQueryStore={usersGridUIStore.requestParams}
                            className={styles.summaryPanel}
                        />
                    }
                    <div className={styles.buttonsWrapper}>
                        <div className={styles.invite}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PersonAddIcon />}
                                disabled={!userPermissionsUIStore.canCreateUser}
                                onClick={onInviteUserModalOpen}
                            >
                                {messages['usersPage.inviteUser.title']}
                            </Button>
                        </div>
                        {(userPermissionsUIStore.isSuperAdmin ||
                            userPermissionsUIStore.isMultiAdmin) && (
                            <div className={classNames(styles.importButton)}>
                                <UsersImport />
                            </div>
                        )}
                        <div className={classNames(styles.exportButton)}>
                            <UsersExport />
                        </div>
                    </div>
                    <UsersFiltersModal
                        isOpen={filtersModalOpen}
                        onRequestClose={() => toggleFiltersModalOpen(false)}
                    />
                </div>
            </div>
            <UsersList />
        </div>
    );
};
